import { User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { createLogger } from './logger';

const logger = createLogger({ module: 'AdminUtils', level: 'info' });

// Cache admin status to avoid repeated database queries
const adminStatusCache: Map<string, { isAdmin: boolean, timestamp: number }> = new Map();
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

export async function isUserAdmin(user: User | null): Promise<boolean> {
  if (!user?.id) return false;
  
  // Check cache first
  const cachedStatus = adminStatusCache.get(user.id);
  if (cachedStatus && (Date.now() - cachedStatus.timestamp) < CACHE_EXPIRY) {
    return cachedStatus.isAdmin;
  }
  
  try {
    // Check if user has admin type in the users table
    const { data: userData, error } = await supabase
      .from('users')
      .select('user_type')
      .eq('id', user.id)
      .single();

    if (error) {
      logger.error('Error checking admin status:', error);
      return false;
    }

    const isAdmin = userData?.user_type === 'admin';
    
    // Cache the result
    adminStatusCache.set(user.id, {
      isAdmin,
      timestamp: Date.now()
    });

    logger.debug('Admin check result:', { userId: user.id, isAdmin });
    return isAdmin;
  } catch (e) {
    logger.error('Exception checking admin status:', e);
    return false;
  }
}

// Synchronous version that returns cached value or false
// Used for initial renders before async check completes
export function isUserAdminSync(user: User | null): boolean {
  if (!user?.id) return false;
  
  const cachedStatus = adminStatusCache.get(user.id);
  return cachedStatus?.isAdmin || false;
}